import React, { useRef, useCallback, useMemo } from "react";
import { createButtonPressAnimation } from "../../../../utilities";
import { Sprite } from "@pixi/react";
import * as PIXI from "pixi.js";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import { SLIDER_BUTTON_CONFIG } from "../../../../textureConfigs";
import { SOUND_NAME } from "../../../../sounds";
import { soundManager } from "../../../../SoundManager";
import { ASSET_NAME } from "../../../../assets";

const SliderButton = ({
  texture,
  x,
  y,
  onClick,
  direction = "left",
  viewportConfig,
}) => {
  const { getResponsiveConfig } = useDimensions();

  // Determine texture based on direction if texture prop is not provided
  const buttonTexture = useMemo(() => {
    if (texture) {
      return texture; // Use provided texture if available
    }
    // Otherwise, select texture based on direction
    const textureName =
      direction === "left"
        ? ASSET_NAME.icon_chevron_left
        : ASSET_NAME.icon_chevron_right;
    return PIXI.Assets.get(textureName);
  }, [texture, direction]);

  // Add ref for the sprite to animate
  const buttonSpriteRef = useRef(null);

  // Use ref for hover and press state to avoid unnecessary re-renders
  const isHoveredRef = useRef(false);
  const isPressedRef = useRef(false);

  // Get responsive configuration from context
  const buttonConfig = getResponsiveConfig(SLIDER_BUTTON_CONFIG);

  // Calculate x and y positions based on direction and viewportConfig
  const buttonPosition = useMemo(() => {
    // If x and y are explicitly provided, use them (for manual override)
    if (x !== undefined && y !== undefined) {
      return { x, y };
    }

    // If viewportConfig is provided, calculate position based on direction
    if (viewportConfig) {
      const calculatedX =
        direction === "left"
          ? -viewportConfig.maxWidth / 2 - buttonConfig.width * 0.5
          : viewportConfig.maxWidth / 2 + buttonConfig.width * 0.5;
      const calculatedY = viewportConfig.viewportHeight / 2;
      return { x: calculatedX, y: calculatedY };
    }

    // Default to 0, 0 if nothing is provided
    return { x: 0, y: 0 };
  }, [x, y, direction, viewportConfig, buttonConfig.width]);

  const handleClick = useCallback(() => {
    if (onClick) {
      soundManager.play(SOUND_NAME.button_click);

      // Set pressed state and update tint
      isPressedRef.current = true;
      if (buttonSpriteRef.current) {
        buttonSpriteRef.current.tint = 0xffffff;
      }

      // Add press animation before calling onClick
      if (buttonSpriteRef.current) {
        createButtonPressAnimation(buttonSpriteRef.current);
      }

      onClick();

      // Reset pressed state after a short delay
      setTimeout(() => {
        isPressedRef.current = false;
        if (buttonSpriteRef.current) {
          buttonSpriteRef.current.tint = isHoveredRef.current
            ? 0xffffff
            : 0xfeff00;
        }
      }, 200);
    }
  }, [onClick]);

  // Hover effect handlers - using refs for direct sprite manipulation
  const handlePointerOver = useCallback(() => {
    if (buttonSpriteRef.current) {
      isHoveredRef.current = true;
      // Change tint to white on hover
      buttonSpriteRef.current.tint = 0xffffff;
    }
  }, []);

  const handlePointerOut = useCallback(() => {
    if (buttonSpriteRef.current) {
      isHoveredRef.current = false;
      // Change tint back to yellow if not pressed
      if (!isPressedRef.current) {
        buttonSpriteRef.current.tint = 0xfeff00;
      }
    }
  }, []);

  if (!buttonTexture) {
    return null;
  }

  return (
    <Sprite
      ref={buttonSpriteRef}
      texture={buttonTexture}
      name="secondary_slider_button"
      anchor={0.5}
      x={buttonPosition.x}
      y={buttonPosition.y}
      width={buttonConfig.width}
      height={buttonConfig.height}
      eventMode="static"
      cursor="pointer"
      alpha={1}
      tint={0xfeff00}
      onpointertap={handleClick}
      onpointerover={handlePointerOver}
      onpointerout={handlePointerOut}
    />
  );
};

export default SliderButton;
